import { useGateways } from "core/gateways-context";
import { ProviderRefDataKeys } from "core/gateways/practice/ProviderGateway";
import {
  cacheUpdateItemArray,
  cacheUpsertSingleItemInArray
} from "core/gateways/ReactQueryCacheHelpers";
import { RefDataModel } from "core/models";
import {
  ProviderAppointmentTypeDto,
  ProviderDto,
  ProviderLocationsDto
} from "modules/booking/models";
import { AppointmentTypeAvailabilityDto } from "modules/booking/models/appointmentTypeAvailability.model";
import {
  PatientConfigRequestDto,
  PatientConfigResponseDto
} from "modules/booking/models/patientConfig.model";
import { useCallback } from "react";

import { HttpError } from "@bps/http-client";
import {
  DefinedInitialDataOptions,
  QueryClient,
  skipToken,
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult
} from "@tanstack/react-query";

import { handleError } from "../../../common/apiErrors";
import { ProviderOnlineStatus } from "../../models/enums";
import { useCoreStore, useFeedbackStore } from "../../stores";

export const ProviderCacheKeys = {
  AppointmentTypeAvailability: "appointment-type-availabilities",
  LocationProviders: "location-providers",
  RefData: "ref-data",
  PatientConfig: "patient-config",
  ProviderAppointmentTypes: "provider-appointment-types",
  ProviderLocations: "provider-locations"
};

export const usePatientConfigQuery = (
  providerId: string,
  appointmentTypeId: string
): UseQueryResult<PatientConfigResponseDto> => {
  const { locationId } = useCoreStore();

  const { providerApi } = useGateways();

  return useQuery({
    queryKey: [
      ProviderCacheKeys.PatientConfig,
      locationId,
      providerId,
      appointmentTypeId
    ],
    queryFn: !locationId
      ? skipToken
      : async () =>
          await providerApi.getPatientConfig(
            providerId,
            locationId,
            appointmentTypeId
          ),
    enabled: !!locationId
  });
};

export const usePatientConfigMutation = () => {
  const queryClient = useQueryClient();
  const { providerApi } = useGateways();
  const feedback = useFeedbackStore();

  return useMutation<
    PatientConfigResponseDto,
    HttpError,
    {
      locationId: string;
      providerId: string;
      appointmentTypeId: string;
      request: PatientConfigRequestDto;
    }
  >({
    mutationFn: ({ locationId, providerId, appointmentTypeId, request }) =>
      providerApi.updatePatientConfig({
        providerId,
        locationId,
        appointmentTypeId,
        request
      }),
    onSuccess: (
      response: PatientConfigResponseDto,
      { locationId, providerId, appointmentTypeId }
    ) => {
      queryClient.setQueryData(
        [
          ProviderCacheKeys.PatientConfig,
          locationId,
          providerId,
          appointmentTypeId
        ],
        response
      );

      updateProviderandProviderList(locationId, providerId, queryClient);

      feedback.success("Patient Config have been updated.");
    },
    onError: error => handleError(error, "Patient Config update")
  });
};

const updateProviderandProviderList = (
  locationId: string,
  providerId: string,
  queryClient: QueryClient
) => {
  // Re-fetch cache for provider appointment types table
  queryClient.refetchQueries({
    queryKey: [
      ProviderCacheKeys.ProviderAppointmentTypes,
      locationId,
      providerId
    ]
  });

  // Invalidate cache for providers table
  queryClient.invalidateQueries({
    queryKey: [ProviderCacheKeys.LocationProviders, locationId]
  });
};

// Remove this customised appointment type from our patient config so we don't query for it
export const useRemovePatientConfig = () => {
  const queryClient = useQueryClient();
  return (
    locationId: string,
    providerId: string,
    appointmentTypeId: string
  ) => {
    queryClient.removeQueries({
      queryKey: [
        ProviderCacheKeys.PatientConfig,
        locationId,
        providerId,
        appointmentTypeId
      ]
    });
  };
};

export const useProviderLocationsQuery = (
  options?: DefinedInitialDataOptions<ProviderLocationsDto[], HttpError>
) => {
  const { providerApi } = useGateways();
  return useQuery<ProviderLocationsDto[], HttpError>({
    queryKey: [ProviderCacheKeys.ProviderLocations],
    queryFn: providerApi.getProviderLocations,
    ...options
  });
};

export const useProvidersRefDataQuery = (
  options?: DefinedInitialDataOptions<Map<string, RefDataModel>, HttpError>
) => {
  const { providerApi } = useGateways();
  return useQuery<Map<string, RefDataModel>, HttpError>({
    queryKey: [ProviderCacheKeys.RefData],
    queryFn: async () => {
      const refDataMap = new Map<string, RefDataModel>();
      const keys = Object.keys(ProviderRefDataKeys);
      // Get ref data
      const data = await Promise.all(keys.map(x => providerApi.getRef(x)));

      // Map ref data
      data.forEach((d, idx) => {
        keys[idx] &&
          refDataMap.set(
            keys[idx],
            new RefDataModel(d) // adapt RefDataDto to RefDataModel to have some usefully properties
          );
      });

      return refDataMap;
    },
    ...options,
    gcTime: Infinity,
    staleTime: Infinity
  });
};

export const useLocationProvidersQuery = <TResult = ProviderDto[]>(
  locationId?: string,
  options?: Partial<
    DefinedInitialDataOptions<ProviderDto[], HttpError, TResult>
  >
) => {
  const { providerApi } = useGateways();
  const core = useCoreStore();
  const queryLocationId = locationId ?? core.locationId;
  return useQuery<ProviderDto[], HttpError, TResult>({
    queryKey: [ProviderCacheKeys.LocationProviders, queryLocationId],
    queryFn: !queryLocationId
      ? skipToken
      : async () => await providerApi.getProvidersForLocation(queryLocationId),
    enabled: !!queryLocationId,
    ...options
  });
};

export const useLocationProviderQuery = (
  providerId?: string
): UseQueryResult<ProviderDto | undefined> => {
  const options: Partial<
    DefinedInitialDataOptions<ProviderDto[], HttpError, ProviderDto | undefined>
  > = {
    select: useCallback(
      (data: ProviderDto[]) => data.find(p => p.id === providerId),
      [providerId]
    )
  };

  return useLocationProvidersQuery<ProviderDto | undefined>(undefined, options);
};

export const useLocationProviderMutation = () => {
  const queryClient = useQueryClient();
  const { providerApi } = useGateways();
  const feedback = useFeedbackStore();
  const { locationId } = useCoreStore();

  return useMutation<ProviderDto, unknown, ProviderDto>({
    mutationFn: provider => {
      const providerOnlineStatus =
        provider.appointmentTypes.length <= 0
          ? ProviderOnlineStatus.no
          : provider.providerOnlineStatus;

      return providerApi.updateProviderForLocation({
        ...provider,
        providerOnlineStatus,
        locationId: locationId!
      });
    },
    onError: err => {
      handleError(err, "Provider update");
    },
    onSuccess: async (provider: ProviderDto) => {
      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [ProviderCacheKeys.LocationProviders, locationId],
        item: provider
      });
      // Re-fetch cache for provider appointment types table
      await queryClient.refetchQueries({
        queryKey: [ProviderCacheKeys.ProviderAppointmentTypes]
      });

      // Re-fetch patient config cache for default on/off state for existing and new patients.
      await queryClient.refetchQueries({
        queryKey: [ProviderCacheKeys.PatientConfig]
      });

      await queryClient.invalidateQueries({
        queryKey: [ProviderCacheKeys.AppointmentTypeAvailability]
      });

      if (
        (provider.providerOnlineStatus === ProviderOnlineStatus.yes ||
          provider.providerOnlineStatus === ProviderOnlineStatus.callClinic) &&
        provider.appointmentTypes.length <= 0
      ) {
        feedback.info(
          "Appointments need to be allocated to a provider for them to be listed online."
        );
      } else {
        feedback.success("Provider details have been updated.");
      }
    }
  });
};

export const useUpdateProviderPhotoMutation = () => {
  const { providerApi } = useGateways();

  return useMutation<
    string,
    HttpError,
    { locationId: string; providerId: string; file: File }
  >({
    mutationFn: ({ locationId, providerId, file }) =>
      providerApi.uploadProviderPhoto(locationId, providerId, file)
  });
};

export const useAppointmentTypeAvailabilitiesQuery = (
  providerId: string,
  appointmentTypeId: string
): UseQueryResult<AppointmentTypeAvailabilityDto[]> => {
  const { providerApi } = useGateways();
  const { locationId } = useCoreStore();

  return useQuery({
    queryKey: [
      ProviderCacheKeys.AppointmentTypeAvailability,
      locationId,
      providerId,
      appointmentTypeId
    ],
    queryFn: !locationId
      ? skipToken
      : async () =>
          await providerApi.getAppointmentTypeAvailabilities(
            providerId,
            locationId,
            appointmentTypeId
          ),
    enabled: !!locationId
  });
};

export const useProviderAppointmentTypesQuery = (
  providerId: string
): UseQueryResult<ProviderAppointmentTypeDto[]> => {
  const { providerApi } = useGateways();
  const { locationId } = useCoreStore();

  return useQuery({
    queryKey: [
      ProviderCacheKeys.ProviderAppointmentTypes,
      locationId,
      providerId
    ],
    queryFn: !locationId
      ? skipToken
      : async () =>
          await providerApi.getAppointmentTypesForProvider(
            providerId,
            locationId
          ),
    enabled: !!locationId
  });
};

export const useCreateAppointmentTypeAvailabilityMutation = () => {
  const queryClient = useQueryClient();
  const { providerApi } = useGateways();
  const feedback = useFeedbackStore();

  return useMutation<
    AppointmentTypeAvailabilityDto,
    HttpError,
    AppointmentTypeAvailabilityDto
  >({
    mutationFn: payload =>
      providerApi.createAppointmentTypeAvailability(payload),
    onSuccess: async (availability: AppointmentTypeAvailabilityDto) => {
      const { locationId, providerId, appointmentTypeId } = availability;
      updateProviderandProviderList(locationId, providerId, queryClient);
      await queryClient.invalidateQueries({
        queryKey: [ProviderCacheKeys.AppointmentTypeAvailability]
      });
      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [
          ProviderCacheKeys.AppointmentTypeAvailability,
          locationId,
          providerId,
          appointmentTypeId
        ],
        item: availability
      });

      feedback.success(
        "Appointment type availability details have been created."
      );
    },
    onError: error => handleError(error, "Appointment type availability create")
  });
};

export const useDeleteAppointmentTypeAvailabilityMutation = (
  providerId: string,
  appointmentTypeId: string
) => {
  const queryClient = useQueryClient();
  const { locationId } = useCoreStore();
  const { providerApi } = useGateways();

  return useMutation<
    boolean,
    unknown,
    { appointmentTypeAvailabilityId: string; removeAppointmentType: boolean }
  >({
    mutationFn: ({ appointmentTypeAvailabilityId, removeAppointmentType }) =>
      providerApi.deleteAppointmentTypeAvailability(
        appointmentTypeAvailabilityId,
        removeAppointmentType
      ),
    onSuccess: (success, request) => {
      updateProviderandProviderList(locationId!, providerId, queryClient);
      queryClient.invalidateQueries({
        queryKey: [ProviderCacheKeys.AppointmentTypeAvailability]
      });
      if (success) {
        cacheUpdateItemArray(
          queryClient,
          [
            ProviderCacheKeys.AppointmentTypeAvailability,
            locationId,
            providerId,
            appointmentTypeId
          ],
          cached =>
            cached.filter(
              prev => prev.id !== request.appointmentTypeAvailabilityId
            )
        );
      }
    }
  });
};

export const useUpdateAppointmentTypeAvailabilityMutation = () => {
  const queryClient = useQueryClient();
  const { providerApi } = useGateways();
  const feedback = useFeedbackStore();

  return useMutation<
    AppointmentTypeAvailabilityDto,
    HttpError,
    AppointmentTypeAvailabilityDto
  >({
    mutationFn: payload =>
      providerApi.updateAppointmentTypeAvailability(payload),
    onError: error =>
      handleError(error, "Appointment type availability update"),
    onSuccess: (availability: AppointmentTypeAvailabilityDto) => {
      cacheUpsertSingleItemInArray({
        queryClient,
        queryKey: [
          ProviderCacheKeys.AppointmentTypeAvailability,
          availability.locationId,
          availability.providerId,
          availability.appointmentTypeId
        ],
        item: availability
      });

      feedback.success(
        "Appointment type availability details have been updated."
      );
    }
  });
};
